<template>
  <div :class="fullView ? 'full-view' : 'claims-value-and-count-echarts'">
    <v-chart class="chart" :option="option" ref="chartRef" :init-options="{ renderer: 'svg' }"/>
  </div>
</template>

<script>
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
  GraphicComponent
} from 'echarts/components'
import { SVGRenderer } from 'echarts/renderers'
import VChart from 'vue-echarts'
import {
  COLOR_PALETTE,
  capitalizeFirstLetter,
  lightenDarkenColor,
  getDefaultChartTextStyle,
  getBarNoDataOptions
} from '@/helpers'
import { useChartResize } from '@/composables/useChartResize'
import { emitChartFinished } from '@/utils/insightsPdfUtils'

use([TooltipComponent, GridComponent, LegendComponent, BarChart, SVGRenderer, GraphicComponent])

export default {
  name: 'ClaimsValueAndCountChart',
  components: {
    VChart
  },
  props: {
    resultSet: {
      type: Object,
      required: true,
      default: () => ({})
    },
    fullView: {
      type: Boolean
    }
  },
  setup () {
    const { chartRef, handleResize } = useChartResize()
    return { chartRef, handleResize }
  },
  data () {
    return {
      option: {
        animation: !this.fullView,
        title: {
          text: this.$t('insights.charts.claimsValueAndCount.title'),
          top: 0,
          left: 'center',
          textStyle: getDefaultChartTextStyle(this.fullView)
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: [
            this.$t('insights.charts.claimsValueAndCount.value'),
            this.$t('insights.charts.claimsValueAndCount.count')
          ],
          bottom: '0',
          left: 'center',
          textStyle: getDefaultChartTextStyle(this.fullView)
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          containLabel: true
        },
        xAxis: [
          {
            type: 'category',
            data: [],
            axisTick: {
              alignWithLabel: true
            },
            axisLabel: {
              interval: 0,
              rotate: 90,
              fontSize: 10,
              show: this.fullView
            }
          }
        ],
        yAxis: [
          {
            type: 'value',
            name: 'Value',
            position: 'left',
            axisLine: {
              lineStyle: {
                color: COLOR_PALETTE[0]
              }
            },
            axisLabel: {
              formatter: '{value}'
            }
          },
          {
            type: 'value',
            name: 'Count',
            position: 'right',
            axisLine: {
              lineStyle: {
                color: COLOR_PALETTE[1]
              }
            },
            axisLabel: {
              formatter: '{value}'
            }
          }
        ],
        series: [
          {
            name: this.$t('insights.charts.claimsValueAndCount.value'),
            type: 'bar',
            barWidth: '40%',
            yAxisIndex: 0,
            data: [],
            itemStyle: {
              color: COLOR_PALETTE[0],
              borderWidth: 1,
              borderColor: lightenDarkenColor(COLOR_PALETTE[0], -10)
            }
          },
          {
            name: this.$t('insights.charts.claimsValueAndCount.count'),
            type: 'bar',
            barWidth: '40%',
            yAxisIndex: 1,
            data: [],
            itemStyle: {
              color: COLOR_PALETTE[1],
              borderWidth: 1,
              borderColor: lightenDarkenColor(COLOR_PALETTE[1], -10)
            }
          }
        ]
      }
    }
  },
  methods: {
    formatCentsToAmount (cents) {
      return (parseInt(cents) / 100).toFixed(2)
    },
    updateData () {
      const noDataOptions = getBarNoDataOptions(this.resultSet, this.fullView, this.option)
      if (noDataOptions) {
        this.option = { ...this.option, ...noDataOptions, title: this.option.title }
        emitChartFinished(this, true)
        return
      }

      const pivot = this.resultSet.chartPivot()
      const data = pivot.map(r => {
        let contractor = r.xValues?.[0] || ''
        contractor = capitalizeFirstLetter(contractor)
        const currency = r.xValues?.[1] || ''
        return {
          contractor,
          currency,
          value: this.formatCentsToAmount(r['variations_parts.agreedvaluecents']) || 0,
          count: r['variations.count'] || 0
        }
      })

      const groupedData = data.reduce((acc, item) => {
        if (!acc[item.contractor]) acc[item.contractor] = []
        acc[item.contractor].push(item)
        return acc
      }, {})

      const sortedGroups = Object.values(groupedData).sort((a, b) => {
        const maxValueA = Math.max(...a.map(d => d.value))
        const maxValueB = Math.max(...b.map(d => d.value))
        return maxValueB - maxValueA
      })

      const sortedData = sortedGroups.flat()

      this.option.xAxis[0].data = sortedData.map(d => `${d.contractor}, ${d.currency}`)

      this.option.series[0].data = sortedData.map((d, index) => ({
        value: d.value,
        itemStyle: {
          color: lightenDarkenColor(COLOR_PALETTE[0], index * 3),
          borderWidth: 1,
          borderColor: COLOR_PALETTE[0]
        }
      }))

      this.option.series[1].data = sortedData.map((d, index) => ({
        value: d.count,
        itemStyle: {
          color: lightenDarkenColor(COLOR_PALETTE[1], index * 3),
          borderWidth: 1,
          borderColor: COLOR_PALETTE[1]
        }
      }))

      const values = sortedData.map(d => d.value)
      const counts = sortedData.map(d => d.count)
      const minValue = Math.min(...values)
      const maxValue = Math.max(...values)
      const maxCount = Math.max(...counts)

      this.option.yAxis[0].min = undefined
      this.option.yAxis[0].max = undefined
      this.option.yAxis[1].min = undefined
      this.option.yAxis[1].max = undefined

      this.option.yAxis[0].axisLabel = {
        formatter: '{value}',
        showMinLabel: false,
        showMaxLabel: false
      }
      this.option.yAxis[1].axisLabel = {
        formatter: '{value}',
        showMinLabel: false,
        showMaxLabel: false
      }

      if (minValue < 0 && maxValue > 0 && maxCount > 0) {
        const rangeValue = maxValue - minValue
        const zeroPosition = (0 - minValue) / rangeValue
        const rightMin = (zeroPosition * maxCount) / (zeroPosition - 1)

        this.option.yAxis[0].min = minValue
        this.option.yAxis[0].max = maxValue
        this.option.yAxis[1].min = rightMin
        this.option.yAxis[1].max = maxCount

        this.option.yAxis[1].axisLabel = {
          formatter: (value) => value < 0 ? '' : value
        }
      } else {
        this.option.yAxis[0].min = minValue >= 0 ? 0 : undefined
        this.option.yAxis[1].min = 0

        if (this.option.yAxis[1].axisLabel.formatter) {
          delete this.option.yAxis[1].axisLabel.formatter
        }
      }
      emitChartFinished(this, false)
    }
  },
  watch: {
    resultSet: {
      handler () {
        this.updateData()
      },
      deep: true
    }
  },
  mounted () {
    this.updateData()
  }
}
</script>

<style scoped>
.claims-value-and-count-echarts {
  height: 250px;
}
.full-view {
  height: var(--full-view-chart-height);
  width: var(--full-view-chart-width);
}
</style>
